import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiControlDirective } from '@template-ui/directives';
import type { WorkflowReasonChoice } from '@app/shared/workflow-commands/workflow-reason-catalog';
let reasonControlSequence = 0;

/** The owning action supplies an evaluated catalog; this control never queries or invents reasons. */
@Component({
  selector: 'app-workflow-reason-code',
  imports: [CommonModule, FormsModule, UiControlDirective],
  template: `<div class="reason-code">
    <label [for]="inputId">{{ label }}</label>
    <select
      uiControl
      [id]="inputId"
      [name]="inputId"
      [ngModel]="value"
      [ngModelOptions]="standalone"
      (ngModelChange)="choose($event)"
      [disabled]="disabled || !!error || !choices.length"
      [attr.aria-required]="required"
      [attr.aria-describedby]="error || validationError ? inputId + '-error' : null"
      [attr.aria-invalid]="error || validationError ? 'true' : null"
    >
      <option value="">Choose a reason</option>
      <option *ngFor="let choice of choices" [value]="choice.code">
        {{ choice.label }} ({{ choice.code }})
      </option>
    </select>
    <p *ngIf="error || validationError" [id]="inputId + '-error'" role="alert">{{ error || validationError }}</p>
  </div>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
      }
      .reason-code,
      label {
        display: grid;
        gap: 0.35rem;
      }
      select {
        width: 100%;
      }
      p {
        margin: 0;
        color: var(--custom-text-color-error);
      }
      select:focus-visible {
        outline: 2px solid var(--custom-focus-ring-color);
        outline-offset: 2px;
      }
    `,
  ],
})
export class WorkflowReasonCodeComponent {
  @Input() inputId = `workflow-reason-${++reasonControlSequence}`;
  @Input() label = 'Reason code';
  @Input() value = '';
  @Input() choices: readonly WorkflowReasonChoice[] = [];
  @Input() disabled = false;
  @Input() error = '';
  @Input() validationError = '';
  @Input() required = false;
  @Output() valueChange = new EventEmitter<string>();
  readonly standalone = { standalone: true };

  choose(value: string): void {
    if (
      this.disabled ||
      this.error ||
      (value !== '' && !this.choices.some((choice) => choice.code === value))
    )
      return;
    this.valueChange.emit(value);
  }
}
